import { useState, useMemo, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Download, Plus, Search, X } from 'lucide-react'
import { useCreateInventoryItem, useInventoryItems } from '@/hooks/inventory'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { EmptyData } from '@/components/EmptyState'
import { useToast } from '@/components/ToastProvider'
import { getApiErrorMessage } from '@/api/client'
import type { CreateInventoryItemRequest } from '@/api/types/inventory'

const emptyPartForm: CreateInventoryItemRequest = {
  skuCode: '',
  partName: '',
  specification: '',
  unit: '件',
  warehouseCode: 'WH-MED-IMAGING',
  safetyStockQty: 0,
}

function CreatePartModal({ onClose }: { onClose: () => void }) {
  const toast = useToast()
  const createMutation = useCreateInventoryItem()
  const [form, setForm] = useState<CreateInventoryItemRequest>(emptyPartForm)
  const fieldClass =
    'mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100'

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      await createMutation.mutateAsync({
        ...form,
        skuCode: form.skuCode.trim(),
        partName: form.partName.trim(),
        specification: form.specification?.trim() || undefined,
        unit: form.unit.trim(),
        warehouseCode: form.warehouseCode.trim(),
      })
      toast.success('配件信息已新增')
      onClose()
    } catch (error) {
      toast.error(getApiErrorMessage(error))
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 p-6 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-part-title"
    >
      <form onSubmit={handleSubmit} className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-start justify-between border-b border-gray-100 bg-white p-6">
          <div>
            <h2 id="create-part-title" className="text-lg font-bold text-gray-900">
              新增配件信息
            </h2>
            <p className="mt-1 text-sm text-gray-500">建立配件基础资料，初始库存默认为 0。</p>
          </div>
          <button type="button" onClick={onClose} aria-label="关闭" className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid gap-5 p-6 sm:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">
            配件编码 <span className="text-red-500">*</span>
            <input
              required
              autoFocus
              value={form.skuCode}
              onChange={(event) => setForm({ ...form, skuCode: event.target.value })}
              placeholder="例如：CT-XRAY-TUBE-002"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            配件名称 <span className="text-red-500">*</span>
            <input
              required
              value={form.partName}
              onChange={(event) => setForm({ ...form, partName: event.target.value })}
              placeholder="例如：CT X射线球管总成"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium text-gray-700 sm:col-span-2">
            规格型号
            <input
              value={form.specification || ''}
              onChange={(event) => setForm({ ...form, specification: event.target.value })}
              placeholder="填写适用品牌、设备型号或关键规格"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            单位 <span className="text-red-500">*</span>
            <input
              required
              value={form.unit}
              onChange={(event) => setForm({ ...form, unit: event.target.value })}
              placeholder="件 / 个 / 套"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            仓库编码 <span className="text-red-500">*</span>
            <input
              required
              value={form.warehouseCode}
              onChange={(event) => setForm({ ...form, warehouseCode: event.target.value })}
              placeholder="例如：WH-MED-IMAGING"
              className={fieldClass}
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            安全库存 <span className="text-red-500">*</span>
            <input
              required
              type="number"
              min="0"
              step="1"
              value={form.safetyStockQty}
              onChange={(event) => setForm({ ...form, safetyStockQty: Math.max(0, Number(event.target.value) || 0) })}
              className={fieldClass}
            />
          </label>
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-100 bg-white px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
            取消
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {createMutation.isPending ? '保存中...' : '保存配件信息'}
          </button>
        </div>
      </form>
    </div>
  )
}

function PartsList() {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const { data: inventoryItems, isLoading, error } = useInventoryItems()

  // Transform inventory items to parts format
  const parts = useMemo(() => {
    if (!inventoryItems) return []

    return inventoryItems.map((item) => {
      const stockRatio = item.availableQty / item.safetyStockQty
      let status: 'out' | 'low' | 'normal-low' | 'ok' = 'ok'
      if (item.availableQty === 0) status = 'out'
      else if (item.availableQty < item.safetyStockQty) status = 'low'
      else if (stockRatio < 1.5) status = 'normal-low'

      // Determine category from part name or specification
      const partName = item.partName.toLowerCase()
      let category = '通用配件'
      if (partName.includes('mri') || partName.includes('核磁') || partName.includes('线圈')) category = 'MRI配件'
      else if (partName.includes('ct') || partName.includes('探测器') || partName.includes('球管')) category = 'CT配件'
      else if (partName.includes('x光') || partName.includes('xray') || partName.includes('射线')) category = 'X光配件'
      else if (partName.includes('dsa') || partName.includes('血管')) category = 'DSA配件'
      else if (partName.includes('超声') || partName.includes('probe')) category = '超声配件'

      return {
        id: String(item.id),
        name: item.partName,
        code: item.skuCode,
        category: category,
        brand: '通用',
        model: item.specification || '-',
        unit: item.unit,
        purchasePrice: 0, // Not in current model
        currentStock: item.availableQty,
        safetyStock: item.safetyStockQty,
        status: status,
        skuCode: item.skuCode,
      }
    })
  }, [inventoryItems])

  // Filter by search keyword
  const filteredParts = useMemo(() => {
    if (!searchKeyword) return parts

    const keyword = searchKeyword.toLowerCase()
    return parts.filter(
      (part) =>
        part.name.toLowerCase().includes(keyword) ||
        part.code.toLowerCase().includes(keyword) ||
        part.model.toLowerCase().includes(keyword)
    )
  }, [parts, searchKeyword])

  const categoryIcons: Record<string, string> = {
    'MRI配件': '🩻',
    'CT配件': '💿',
    'X光配件': '📷',
    'DSA配件': '🔬',
    '超声配件': '📠',
    '通用配件': '🔧',
  }

  const statusConfig: Record<string, { label: string; class: string; stockClass: string }> = {
    out: { label: '缺货', class: 'bg-red-100 text-red-600', stockClass: 'text-red-600' },
    low: { label: '低库存', class: 'bg-orange-100 text-orange-600', stockClass: 'text-orange-600' },
    'normal-low': { label: '正常偏低', class: 'bg-yellow-100 text-yellow-600', stockClass: 'text-yellow-600' },
    ok: { label: '充足', class: 'bg-green-100 text-green-600', stockClass: 'text-green-600' },
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载配件数据..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <InlineError message="加载失败，请重试" />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/supply-chain/inventory/list" className="p-2 hover:bg-gray-100 rounded-lg transition">
              <Search className="w-5 h-5 text-gray-600 rotate-180" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">配件信息</h1>
              <p className="text-sm text-gray-500 mt-1">维护物料编码、规格、单位与安全库存基线</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2">
              <Download className="w-4 h-4" />
              导出
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              新增配件信息
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="搜索配件名称、型号、编码..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option>全部分类</option>
            <option>CT配件</option>
            <option>MRI配件</option>
            <option>DSA配件</option>
            <option>超声配件</option>
            <option>X光配件</option>
          </select>
          <select className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option>全部品牌</option>
            <option>西门子</option>
            <option>GE</option>
            <option>飞利浦</option>
            <option>联影</option>
          </select>
          <select className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option>库存状态</option>
            <option>有库存</option>
            <option>低库存</option>
            <option>无库存</option>
          </select>
          <button className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition">
            重置
          </button>
        </div>
      </div>

      {/* Parts Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        {filteredParts.length === 0 ? (
          <EmptyData description={searchKeyword ? '没有找到符合条件的配件' : '暂无配件数据'} />
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-sm text-gray-500">
                  <th className="px-5 py-4 font-medium">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </th>
                  <th className="px-5 py-4 font-medium">配件信息</th>
                  <th className="px-5 py-4 font-medium">分类/品牌</th>
                  <th className="px-5 py-4 font-medium">规格型号</th>
                  <th className="px-5 py-4 font-medium">单位</th>
                  <th className="px-5 py-4 font-medium">采购价</th>
                  <th className="px-5 py-4 font-medium">库存</th>
                  <th className="px-5 py-4 font-medium">状态</th>
                  <th className="px-5 py-4 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredParts.map((part) => {
                  const status = statusConfig[part.status]
                  return (
                    <tr key={part.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <input type="checkbox" className="rounded border-gray-300" />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-2xl">
                            {categoryIcons[part.category] || '📦'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{part.name}</div>
                            <div className="text-xs text-gray-400">{part.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-gray-600">{part.category}</div>
                        <div className="text-xs text-gray-400">{part.brand}</div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">{part.model}</td>
                      <td className="px-5 py-4 text-gray-600">{part.unit}</td>
                      <td className="px-5 py-4 text-gray-400">-</td>
                      <td className="px-5 py-4">
                        <span className={status.stockClass + ' font-medium'}>{part.currentStock}</span>
                        <span className="text-gray-400">
                          {' '}
                          / {part.safetyStock}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.class}`}>{status.label}</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Link to={`/supply-chain/parts/${part.skuCode}`} className="text-primary-600 hover:underline">
                            详情
                          </Link>
                          <span className="text-gray-300">|</span>
                          <Link to="#" className="text-blue-600 hover:underline">
                            编辑
                          </Link>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                显示 {filteredParts.length} 条，共 {parts.length} 条记录
              </div>
            </div>
          </>
        )}
      </div>

      {createOpen && <CreatePartModal onClose={() => setCreateOpen(false)} />}
    </div>
  )
}

export default PartsList
